import { ArrowLeftIcon } from '@heroicons/react/20/solid'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client'
import { GET_COUNTRY_LIST } from 'graphql/common/queries'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { ADD_SHUTTLE_SERVICE_PROVIDER } from 'graphql/shuttleService/mutations'
import ShuttleServiceFormFields from '../components/ShuttleServiceFormFields'
import { useShuttleServiceForm, formatShuttleServiceData } from '../hooks/useShuttleServiceForm'

export default function ShuttleServiceForm() {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [submitLoading, setSubmitLoading] = useState(false)
    const [countries, setCountries] = useState([])

    const handleBack = () => navigate('/system-config/shuttle-services')
    const handleDialogClose = () => {
        setShowSucDialog(false)
        setShowErrDialog(false)
    }

    const [getCountryList] = useLazyQuery(GET_COUNTRY_LIST, {
        fetchPolicy: 'network-only',
        onCompleted: data => data.country && setCountries(data.country),
    })

    useEffect(() => {
        getCountryList({ variables: { code: null } })
    }, [])

    const [addShuttleService] = useMutation(ADD_SHUTTLE_SERVICE_PROVIDER, {
        onCompleted: data => {
            formik.setSubmitting(false)
            setSubmitLoading(false)
            if (data?.addShuttleServiceProvider.code === '200' && data?.addShuttleServiceProvider.success) {
                setShowSucDialog(true)
                setTimeout(handleBack, 500)
            } else {
                setErrorMessage(data?.addShuttleServiceProvider.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            formik.setSubmitting(false)
            setErrorMessage(error.message)
            setShowErrDialog(true)
        },
    })

    const formik = useShuttleServiceForm(
        {
            provider_name: '',
            service_description_en: '',
            service_description_ar: '',
            contact_person: '',
            contact_mobile_number: '',
            contact_email: '',
            contact_address: '',
            country_code: '',
            price_base: '',
            price_tax: '',
            price_currency: 'BHD',
            isactive: true,
        },
        values => {
            setSubmitLoading(true)
            addShuttleService({ variables: formatShuttleServiceData(values) })
        }
    )

    return (
        <>
            <div className="mb-8 flex justify-between items-center">
                <button
                    type="button"
                    onClick={handleBack}
                    className="inline-flex items-center gap-x-0.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Back')}
                </button>
                <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={handleBack}
                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                        {t('Cancel')}
                    </button>
                    <button
                        disabled={formik.isSubmitting}
                        onClick={formik.handleSubmit}
                        type="button"
                        className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    >
                        {t('Save')}
                    </button>
                </div>
            </div>
            <form>
                <div className="space-y-6">
                    <ShuttleServiceFormFields formik={formik} countries={countries} />
                    <div className="pt-4 mt-2 border-t border-gray-200 flex justify-end">
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={handleBack}
                                className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                                {t('Cancel')}
                            </button>
                            <button
                                disabled={formik.isSubmitting}
                                onClick={formik.handleSubmit}
                                type="button"
                                className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                            >
                                {t('Save')}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={errorMessage} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog
                    status={true}
                    title={t('Success')}
                    message={t('Shuttle service added successfully')}
                    onClose={handleDialogClose}
                />
            )}
            {submitLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                </div>
            )}
        </>
    )
}
